import { customValueAxis, staticPercent } from './handleCustomValue';

const breakTextIntoNLines = (text, n) => {
  const words = text.toString().split(' ');
  const meanSize = Math.floor(text.toString().length / n);
  const arr = [];
  let acc = 0;
  let subtext = [];
  let count = 0;
  if (words.length <= n) return words;
  words.forEach((w) => {
    count += 1;
    if (count !== words.length) {
      acc += w.length;
      subtext.push(w);
      if (acc >= meanSize) {
        arr.push(subtext.join(' '));
        acc = 0;
        subtext = [];
      }
    } else {
      subtext.push(w);
      arr.push(subtext.join(' '));
    }
  });
  return arr;
};

const customAxisTick = (dt, config, globalConfig, useFormat, axisType) => (
  <g key={`${axisType}-${dt.value}`} transform={`translate(${dt.x}, ${dt.y})`}>
    <line
      x1="0"
      y1="0"
      x2={dt.lineX}
      y2={dt.lineY}
      style={{ stroke: '#777', strokeWidth: '1px' }}
    />
    <text
      dominantBaseline={dt.textBaseline}
      textAnchor={dt.textAnchor}
      transform={`translate(${dt.textX}, ${dt.textY}) rotate(${dt.rotate})`}
      style={{
        fontSize: config?.AxisGlobal?.fontSize,
        fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : config?.AxisGlobal?.fontFamily}', Arial`,
        fill: config?.AxisGlobal?.color,
        outlineWidth: '0px',
        outlineColor: 'transparent',
      }}
    >
      {breakTextIntoNLines(
        useFormat ? customValueAxis(dt.value, config, axisType) : dt.value,
        config?.EnableAxisX?.maxLabelLines || 1,
      ).map((e, i) => (
        <tspan key={e} x="0" dy={`${1.2 * i}em`}>
          {e}
        </tspan>
      ))}
    </text>
  </g>
);

const customAxis = (AxisPos, config, globalConfig, useFormat = true) => {
  switch (AxisPos) {
    case 'top':
      return config?.EnableAxisX?.axisTop ? {
        ...config?.EnableAxisX,
        legendOffset: config?.EnableAxisX?.legendOffset * -1,
        renderTick: (dt) => customAxisTick(dt, config, globalConfig, useFormat, 'X'),
        format: (value) => (useFormat ? customValueAxis(value, config, 'X') : value),
      } : null;
    case 'bottom':
      return config?.EnableAxisX?.axisBot ? {
        ...config?.EnableAxisX,
        renderTick: (dt) => customAxisTick(dt, config, globalConfig, useFormat, 'X'),
        format: (value) => (useFormat ? customValueAxis(value, config, 'X') : value),
      } : null;
    case 'right':
      return config?.EnableAxisY?.axisRight ? {
        ...config?.EnableAxisY,
        legendOffset: config?.EnableAxisY?.legendOffset * -1,
        renderTick: (dt) => customAxisTick(dt, config, globalConfig, useFormat, 'Y'),
        format: (value) => (useFormat ? customValueAxis(value, config, 'Y') : value),
      } : null;
    case 'left':
      return config?.EnableAxisY?.axisLeft ? {
        ...config?.EnableAxisY,
        renderTick: (dt) => customAxisTick(dt, config, globalConfig, useFormat, 'Y'),
        format: (value) => (useFormat ? customValueAxis(value, config, 'Y') : value),
      } : null;
    default:
      break;
  }
  return null;
};

const customPercentAxis = (axisConfig, precision, total) => ({
  ...axisConfig,
  format: (value) => (total !== null ? (
    staticPercent(value / total, axisConfig?.Decimals, precision)
  ) : (
    staticPercent(value, axisConfig?.Decimals, precision)
  )),
});

export const customPercentAxisRight = (config, globalConfig, total = null, useFormat = true) => {
  if (['percentual', 'both-flip'].includes(config?.DataFormatValueType ?? 'percentual')) {
    const conf = {
      ...config?.EnableAxisY ?? {},
      legendOffset: config?.EnableAxisY?.legendOffset * -1,
      Decimals: config?.EnableAxisX?.Decimals,
    };
    return (
      config?.EnableAxisY?.axisRight ? customPercentAxis(
        conf, config?.DecimalsYPercent, total,
      ) : null
    );
  } return customAxis('right', config, globalConfig, useFormat);
};

export const customPercentAxisLeft = (config, globalConfig, total = null, useFormat = true) => {
  if (['percentual', 'both-flip'].includes(config?.DataFormatValueType ?? 'percentual')) {
    const conf = {
      ...config?.EnableAxisY ?? {},
      Decimals: config?.EnableAxisX?.Decimals,
    };
    return (
      config?.EnableAxisY?.axisLeft ? customPercentAxis(
        conf, +config?.DecimalsYPercent, total,
      ) : null);
  } return customAxis('left', config, globalConfig, useFormat);
};

export const customPercentAxisTop = (config, globalConfig, total = null, useFormat = true) => {
  if (['percentual', 'both-flip'].includes(config?.DataFormatValueType ?? 'percentual')) {
    const conf = {
      ...config?.EnableAxisX ?? {},
      legendOffset: config?.EnableAxisX?.legendOffset * -1,
      Decimals: config?.EnableAxisY?.Decimals,
    };
    return (
      config?.EnableAxisX?.axisTop ? customPercentAxis(
        conf, config?.DecimalsXPercent, total,
      ) : null);
  } return customAxis('top', config, globalConfig, useFormat);
};

export const customPercentAxisBottom = (config, globalConfig, total = null, useFormat = true) => {
  if (['percentual', 'both-flip'].includes(config?.DataFormatValueType ?? 'percentual')) {
    const conf = {
      ...config?.EnableAxisX ?? {},
      Decimals: config?.EnableAxisY?.Decimals,
    };
    return (
      config?.EnableAxisX?.axisBot ? customPercentAxis(
        conf, config?.DecimalsXPercent, total,
      ) : null);
  } return customAxis('bottom', config, globalConfig, useFormat);
};

const customIntervalAxis = (config, axisConfig, orientation) => ({
  ...axisConfig,
  format: (value) => (
    `]${value.toString().split('|').map((v) => customValueAxis(v, config, orientation)).join('; ')}]`
  ),
});

export const customIntervalAxisRight = (config) => {
  const axisConfig = {
    ...config?.EnableAxisY ?? {},
    legendOffset: config?.EnableAxisY?.legendOffset * -1,
  };
  return (
    config?.EnableAxisY?.axisRight ? customIntervalAxis(config, axisConfig, 'Y') : null
  );
};

export const customIntervalAxisLeft = (config) => (
  config?.EnableAxisY?.axisLeft ? customIntervalAxis(config, config?.EnableAxisY ?? {}, 'Y') : null
);

export const customIntervalAxisTop = (config) => {
  const axisConfig = {
    ...config?.EnableAxisX ?? {},
    legendOffset: config?.EnableAxisX?.legendOffset * -1,
  };
  return (
    config?.EnableAxisX?.axisTop ? customIntervalAxis(config, axisConfig, 'X') : null
  );
};

export const customIntervalAxisBottom = (config) => (
  config?.EnableAxisX?.axisBot ? customIntervalAxis(config, config?.EnableAxisX ?? {}, 'X') : null
);

export default customAxis;
